import React, { useEffect, useState } from 'react';
import { MdEvent } from 'react-icons/md';
import Listagem, { ConfiguracaoFiltros } from '../../../components/listagem';
import { EventoRiscoLog } from '../../../services/tipos';
import { buscarLog } from '../api';

const CATEGORIA_OPCOES = [
    { label: 'Info', value: 1 },
    { label: 'Notificação', value: 2 },
    { label: 'Ação', value: 3 },
    { label: 'Erro', value: 4 },
];

const CATEGORIA_MAP: Record<number, string> = { 1: 'Info', 2: 'Notificação', 3: 'Ação', 4: 'Erro' };
const CATEGORIA_COR: Record<number, string> = { 1: '#607d8b', 2: '#2e7d32', 3: '#1565c0', 4: '#c62828' };

interface LogTabProps {
    idEmpresa: string;
    filtroVeiculo?: { idVeiculo: string; placa: string } | null;
    onLimparFiltroVeiculo?: () => void;
}

const LogTab: React.FC<LogTabProps> = ({ idEmpresa, filtroVeiculo, onLimparFiltroVeiculo }) => {
    const [itens, setItens] = useState<EventoRiscoLog[]>([]);
    const [carregando, setCarregando] = useState(false);

    // Filtros — usando os tipos nativos do Listagem
    const [categoria, setCategoria] = useState<string | number | boolean | (string | number | boolean)[]>([]);
    const [dataInicio, setDataInicio] = useState<Date | null>(null);
    const [dataFim, setDataFim] = useState<Date | null>(null);

    const carregar = async () => {
        setCarregando(true);
        try {
            const filtros: Record<string, any> = { page: 0, pageSize: 100 };
            const categorias = Array.isArray(categoria) ? categoria : (categoria !== '' ? [categoria] : []);
            if (categorias.length > 0) filtros.categoria = categorias.join(',');
            if (dataInicio) filtros.de = dataInicio.toISOString().split('T')[0];
            if (dataFim) filtros.ate = dataFim.toISOString().split('T')[0];
            if (filtroVeiculo) filtros.idVeiculo = filtroVeiculo.idVeiculo;
            const r = await buscarLog(idEmpresa, filtros);
            setItens(r.itens);
        } finally {
            setCarregando(false);
        }
    };

    // Recarrega ao montar e sempre que o filtro por veículo mudar (via "Ver histórico").
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregar(); }, [filtroVeiculo?.idVeiculo]);

    const colunas = [
        {
            field: 'dataHora',
            headerName: 'Data/Hora',
            width: 170,
            renderCell: (p: any) => new Date(p.value).toLocaleString('pt-BR'),
        },
        {
            field: 'categoria',
            headerName: 'Categoria',
            width: 130,
            renderCell: (p: any) => {
                const cor = CATEGORIA_COR[p.value as number] ?? '#607d8b';
                return (
                    <span style={{
                        backgroundColor: cor, color: '#fff', padding: '2px 8px',
                        borderRadius: 10, fontSize: 12, fontWeight: 600,
                    }}>
                        {CATEGORIA_MAP[p.value as number] ?? p.value}
                    </span>
                );
            },
        },
        {
            field: 'placa',
            headerName: 'Placa',
            width: 110,
        },
        {
            field: 'mensagem',
            headerName: 'Mensagem',
            flex: 1,
        },
        {
            field: 'sucesso',
            headerName: 'OK',
            width: 70,
            renderCell: (p: any) => (
                <span style={{ color: p.value === true ? '#2e7d32' : p.value === false ? '#c62828' : '#999', fontWeight: 700 }}>
                    {p.value === true ? '✓' : p.value === false ? '✗' : '—'}
                </span>
            ),
        },
    ];

    const filtros: ConfiguracaoFiltros = {
        dropdowns: [
            {
                key: 'categoria',
                label: 'Categoria',
                placeholder: 'Todas',
                opcoes: CATEGORIA_OPCOES,
                value: categoria,
                onChange: setCategoria,
                multiSelect: true,
            },
        ],
        datas: {
            dataInicio,
            dataFim,
            onChangeDataInicio: setDataInicio,
            onChangeDataFim: setDataFim,
            labelInicio: 'De',
            labelFim: 'Até',
        },
        aoBuscar: carregar,
        carregando,
    };

    return (
        <>
            {filtroVeiculo && (
                <div style={{ marginBottom: 12 }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        background: '#e3f2fd', color: '#1565c0', padding: '4px 12px',
                        borderRadius: 16, fontSize: 13, fontWeight: 600,
                    }}>
                        Filtrando por: {filtroVeiculo.placa}
                        <span
                            onClick={onLimparFiltroVeiculo}
                            style={{ cursor: 'pointer', fontWeight: 700 }}
                            title="Limpar filtro"
                        >✕</span>
                    </span>
                </div>
            )}
            <Listagem
                carregando={carregando}
                linhas={itens}
                colunas={colunas}
                nomeEntidade="Log de Risco"
                nomePropriedade="mensagem"
                exibirAcoesDeCrud={false}
                ocultarNovo={true}
                handleCriar={() => {}}
                handleSelecionarEntidade={() => {}}
                IconeEntidade={MdEvent}
                filtros={filtros}
            />
        </>
    );
};

export default LogTab;
